import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getToken } from '../../services/auth';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const COLORS = ['#8b5cf6', '#3b82f6', '#14b8a6', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#10b981', '#f97316', '#06b6d4'];

interface SummaryData {
  total_patients: number;
  visits_this_month: number;
  revenue_this_month: number;
}

interface StatsData {
  summary: SummaryData;
  revenue_trend: { month: string; income: number; expense: number }[];
  procedure_distribution: { name: string; count: number }[];
  daily_volume: { date: string; count: number }[];
  new_patients: { month: string; count: number }[];
  gender_distribution: { gender: string; count: number }[];
  visit_types: { type: string; count: number }[];
  referral_sources: { source: string; count: number }[];
}

const cardStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.8)', borderRadius: 16,
  padding: 20, border: '1px solid rgba(255,255,255,0.5)',
  backdropFilter: 'blur(10px)', boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
};

export default function StatsDashboard() {
  const [data, setData] = useState<StatsData | null>(null);
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${getToken()}` };
    setLoading(true);
    axios.get(`/api/stats/overview?months=${months}`, { headers })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [months]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading statistics...</div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No data available</div>;

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ margin: 0, color: '#1e293b', fontSize: 22, fontWeight: 700 }}>📊 Statistics Dashboard</h2>
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))}
          style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
          <option value={3}>Last 3 months</option>
          <option value={6}>Last 6 months</option>
          <option value={12}>Last 12 months</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { icon: '👥', label: 'Total Patients', value: data.summary.total_patients.toLocaleString(), color: '#8b5cf6' },
          { icon: '🏥', label: 'Visits This Month', value: data.summary.visits_this_month.toLocaleString(), color: '#3b82f6' },
          { icon: '💰', label: 'Revenue This Month', value: `₹${data.summary.revenue_this_month.toLocaleString('en-IN')}`, color: '#14b8a6' },
        ].map((s) => (
          <div key={s.label} style={{ ...cardStyle, textAlign: 'center', borderLeft: `4px solid ${s.color}` }}>
            <div style={{ fontSize: 28 }}>{s.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color, margin: '4px 0' }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Revenue Trend */}
        <div style={cardStyle}>
          <h4 style={{ margin: '0 0 12px', color: '#334155', fontSize: 14 }}>💹 Revenue vs Expenses</h4>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={data.revenue_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Area type="monotone" dataKey="income" stroke="#14b8a6" fill="rgba(20,184,166,0.2)" name="Income" />
              <Area type="monotone" dataKey="expense" stroke="#f43f5e" fill="rgba(244,63,94,0.15)" name="Expenses" />
              <Legend />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Procedure Distribution */}
        <div style={cardStyle}>
          <h4 style={{ margin: '0 0 12px', color: '#334155', fontSize: 14 }}>🦷 Top Procedures</h4>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={data.procedure_distribution} dataKey="count" nameKey="name" cx="50%" cy="50%"
                outerRadius={90} label={(e) => `${e.name} (${e.value})`} labelLine={false}>
                {data.procedure_distribution.map((_entry, idx) => (
                  <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Daily Patient Volume */}
        <div style={cardStyle}>
          <h4 style={{ margin: '0 0 12px', color: '#334155', fontSize: 14 }}>📅 Daily Patient Volume</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.daily_volume}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Patients" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* New Patients */}
        <div style={cardStyle}>
          <h4 style={{ margin: '0 0 12px', color: '#334155', fontSize: 14 }}>📈 New Patient Acquisition</h4>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data.new_patients}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} name="New Patients" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Gender Distribution */}
        <div style={cardStyle}>
          <h4 style={{ margin: '0 0 12px', color: '#334155', fontSize: 14 }}>👤 Gender Distribution</h4>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data.gender_distribution} dataKey="count" nameKey="gender" cx="50%" cy="50%"
                outerRadius={75} label>
                {data.gender_distribution.map((_entry, idx) => (
                  <Cell key={idx} fill={['#8b5cf6', '#ec4899', '#6366f1', '#94a3b8'][idx % 4]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Referral Sources */}
        <div style={cardStyle}>
          <h4 style={{ margin: '0 0 12px', color: '#334155', fontSize: 14 }}>📣 Referral Sources</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.referral_sources} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="source" tick={{ fontSize: 11 }} width={80} />
              <Tooltip />
              <Bar dataKey="count" fill="#14b8a6" radius={[0, 4, 4, 0]} name="Patients" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
